import clsx from "clsx";
import React, {
  CSSProperties,
  isValidElement,
  MouseEvent,
  ReactNode
} from "react";
import NoData from "../NoData";
import RenderWhen from "../RenderWhen";
import { ThreeDotsLoader } from "../three-dots-loader";

interface KeyValueItemProps {
  id?: string;
  label: ReactNode;
  value: ReactNode;
  className?: string;
  keyClassName?: string;
  style?: CSSProperties;
  isLoading?: boolean;
  loaderComponent?: ReactNode;
  onClick?: (event: MouseEvent) => void;
  "data-testid"?: string;
  textOverflowProtection?: boolean;
}

export const KeyValueItem: React.FC<KeyValueItemProps> = ({
  id,
  label,
  value,
  className,
  keyClassName,
  style,
  isLoading = false,
  onClick,
  loaderComponent = <ThreeDotsLoader />,
  "data-testid": dataTestId,
  textOverflowProtection = true
}) => (
  <div
    id={id}
    className={className}
    onClick={(event: MouseEvent) => {
      onClick?.(event);
    }}
    data-testid={dataTestId}
  >
    <span
      className={keyClassName}
      style={style || { fontWeight: "bold", marginRight: "2px" }}
    >
      {label}
    </span>
    <RenderWhen condition={isLoading}>
      <span>{loaderComponent}</span>
    </RenderWhen>
    <RenderWhen condition={!isLoading}>
      {isValidElement(value) ? (
        value
      ) : (
        <span className={clsx(textOverflowProtection && "overflow-ellipsis")}>
          {value ?? <NoData />}
        </span>
      )}
    </RenderWhen>
  </div>
);

type KeyValueItemComponent = React.ReactElement<typeof KeyValueItem>;
interface KeyValueListProps {
  children: KeyValueItemComponent | KeyValueItemComponent[];
  className?: string;
  style?: CSSProperties;
  onClick?: (event?: MouseEvent) => void;
  "data-testid"?: string;
}

const KeyValueList: React.FC<KeyValueListProps> & {
  Item: typeof KeyValueItem;
} = ({ children, className, style, onClick, "data-testid": dataTestId }) => (
  <div
    className={className}
    style={style}
    onClick={onClick}
    data-testid={dataTestId}
  >
    {React.Children.map(children, (child, index) => (
      <div key={index}>{child}</div>
    ))}
  </div>
);

KeyValueList.Item = KeyValueItem;

export default KeyValueList;
